'use client';

/**
 * PremiumDeck — home V3 a "pagine intere", 3 modelli provabili live:
 *   A = deck animato (rotella/swipe/tastiera/bottoni → slide animata, niente peek)
 *   B = pagine discrete (solo bottoni/dots/tastiera/swipe, transizione fade)
 *   C = scroll-snap nativo CSS (scroll vero che aggancia; accessibile ma con peek)
 *
 * Accessibilità (A/B): tastiera operabile (frecce/PageDown/Home/End), focus spostato
 * sulla pagina attiva, aria-live di annuncio, prefers-reduced-motion → istantaneo,
 * niente keyboard-trap, controlli espliciti (dots + frecce) sempre presenti.
 * Overflow: ogni pagina ha scroll interno; il cambio-pagina da rotella/tastiera
 * scatta solo quando la pagina interna è già al bordo (pattern fullPage).
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';

export type DeckMode = 'A' | 'B' | 'C';

export interface DeckSlide {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface DeckCtxValue {
  goToId: (id: string) => void;
  mode: DeckMode;
}
const DeckContext = createContext<DeckCtxValue>({ goToId: () => {}, mode: 'C' });
export const useDeck = () => useContext(DeckContext);

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

export default function PremiumDeck({ mode, slides }: { mode: DeckMode; slides: DeckSlide[] }) {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(0);
  const slideRefs = useRef<(HTMLElement | null)[]>([]);
  const lockRef = useRef(false); // debounce transizioni
  const touchStartY = useRef<number | null>(null);

  const indexOfId = useCallback((id: string) => slides.findIndex((s) => s.id === id), [slides]);

  // ----- navigazione comune -----
  const goTo = useCallback((idx: number, focus = true) => {
    const clamped = Math.max(0, Math.min(slides.length - 1, idx));
    setActive(clamped);
    if (typeof window !== 'undefined') {
      const id = slides[clamped]?.id;
      if (id && window.location.hash !== `#${id}`) {
        window.history.replaceState(null, '', `#${id}`);
      }
    }
    if (focus) {
      // focus sulla pagina attiva (screen reader / tastiera atterrano qui)
      requestAnimationFrame(() => slideRefs.current[clamped]?.focus());
    }
  }, [slides]);

  const goToId = useCallback((id: string) => {
    const idx = indexOfId(id);
    if (idx < 0) return;
    if (mode === 'C') {
      document.getElementById(id)?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    } else {
      goTo(idx);
    }
  }, [indexOfId, mode, goTo, reduced]);

  // hash iniziale
  useEffect(() => {
    if (mode === 'C') return;
    const hash = window.location.hash.replace('#', '');
    const idx = hash ? indexOfId(hash) : -1;
    if (idx > 0) setActive(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // CTA hero (e altri) → navigano via evento, indipendente dal modello.
  useEffect(() => {
    const onGoto = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (typeof id === 'string') goToId(id);
    };
    window.addEventListener('deck:goto', onGoto as EventListener);
    return () => window.removeEventListener('deck:goto', onGoto as EventListener);
  }, [goToId]);

  // edge-detection: la pagina interna è già in cima/fondo?
  const atEdge = useCallback((dir: 1 | -1): boolean => {
    const el = slideRefs.current[active];
    if (!el) return true;
    const scroller = el.querySelector<HTMLElement>('[data-deck-scroll]') || el;
    if (scroller.scrollHeight - scroller.clientHeight <= 2) return true; // niente overflow
    if (dir === 1) return scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
    return scroller.scrollTop <= 2;
  }, [active]);

  const step = useCallback((dir: 1 | -1) => {
    if (lockRef.current) return;
    if (!atEdge(dir)) return; // lascia scrollare il contenuto interno
    const next = active + dir;
    if (next < 0 || next >= slides.length) return;
    lockRef.current = true;
    goTo(next);
    setTimeout(() => { lockRef.current = false; }, reduced ? 60 : 720);
  }, [active, atEdge, goTo, slides.length, reduced]);

  // ----- input handlers (solo A/B) -----
  useEffect(() => {
    if (mode === 'C') return;
    const root = document.getElementById('premium-deck-root');
    if (!root) return;

    const onWheel = (e: WheelEvent) => {
      if (mode !== 'A') return; // B: niente trigger da rotella
      const dir: 1 | -1 = e.deltaY > 0 ? 1 : -1;
      if (atEdge(dir)) { e.preventDefault(); step(dir); }
    };
    const onTouchStart = (e: TouchEvent) => { touchStartY.current = e.touches[0]?.clientY ?? null; };
    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartY.current == null) return;
      const dy = touchStartY.current - (e.changedTouches[0]?.clientY ?? touchStartY.current);
      touchStartY.current = null;
      if (Math.abs(dy) < 50) return;
      const dir: 1 | -1 = dy > 0 ? 1 : -1;
      if (atEdge(dir)) step(dir);
    };
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return; // non rubare la tastiera ai form
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { if (atEdge(1)) { e.preventDefault(); step(1); } }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') { if (atEdge(-1)) { e.preventDefault(); step(-1); } }
      else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); goTo(slides.length - 1); }
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
    };
  }, [mode, atEdge, step, goTo, slides.length]);

  // Mode C: aggiorna l'hash mentre scrolli (Indietro browser + link diretti).
  useEffect(() => {
    if (mode !== 'C') return;
    const root = document.getElementById('premium-deck-root');
    if (!root) return;
    const sections = slides.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          const id = (e.target as HTMLElement).id;
          const idx = slides.findIndex((s) => s.id === id);
          if (idx >= 0) setActive(idx);
          if (id && window.location.hash !== `#${id}`) window.history.replaceState(null, '', `#${id}`);
        }
      }
    }, { root, threshold: [0.6] });
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [mode, slides]);

  // ===================== RENDER =====================

  // C: scroll-snap nativo (accessibile, ma peek durante lo scroll)
  if (mode === 'C') {
    return (
      <DeckContext.Provider value={{ goToId, mode }}>
        <div
          id="premium-deck-root"
          className={`relative z-10 h-[100svh] overflow-y-scroll snap-y snap-mandatory premium-snap-noscrollbar ${reduced ? '' : 'scroll-smooth'}`}
          style={{ scrollbarWidth: 'none' }}
        >
          {slides.map((s) => (
            <section key={s.id} id={s.id} className="snap-start h-[100svh] overflow-y-auto" data-deck-scroll>
              {s.content}
            </section>
          ))}
        </div>

        {/* Frecce su/giù */}
        <div className="fixed left-1/2 -translate-x-1/2 bottom-5 z-30 flex flex-col items-center gap-2">
          {active < slides.length - 1 && (
            <button type="button" onClick={() => goToId(slides[active + 1].id)} aria-label="Sezione successiva"
              className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white flex items-center justify-center hover:bg-white/25 transition-colors animate-bounce">
              <ChevronDown size={18} />
            </button>
          )}
        </div>
      </DeckContext.Provider>
    );
  }

  // A/B: deck a pagine. Slide attiva centrata, le altre fuori (niente peek).
  const transitionCls = reduced ? '' : 'transition-all duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]';
  return (
    <DeckContext.Provider value={{ goToId, mode }}>
      <div id="premium-deck-root" className="relative z-10 h-[100svh] overflow-hidden" data-cid="premium-deck">
        {/* annuncio per screen reader */}
        <div aria-live="polite" className="sr-only">{`Pagina ${active + 1} di ${slides.length}: ${slides[active]?.label}`}</div>

        {slides.map((s, i) => {
          const state = i === active ? 'active' : i < active ? 'prev' : 'next';
          // A = slide verticale; B = fade
          const pos = mode === 'A'
            ? (state === 'active' ? 'translate-y-0 opacity-100' : state === 'prev' ? '-translate-y-full opacity-0' : 'translate-y-full opacity-0')
            : (state === 'active' ? 'opacity-100' : 'opacity-0');
          return (
            <section
              key={s.id}
              id={s.id}
              ref={(el) => { slideRefs.current[i] = el; }}
              tabIndex={-1}
              aria-hidden={i !== active}
              aria-label={s.label}
              data-deck-scroll
              className={`absolute inset-0 h-[100svh] overflow-y-auto outline-none ${transitionCls} ${pos} ${i === active ? 'z-10 pointer-events-auto' : 'z-0 pointer-events-none'}`}
            >
              {s.content}
            </section>
          );
        })}

        {/* Frecce su/giù */}
        <div className="fixed left-1/2 -translate-x-1/2 bottom-5 z-30 flex flex-col items-center gap-2">
          {active < slides.length - 1 && (
            <button type="button" onClick={() => goTo(active + 1)} aria-label="Pagina successiva"
              className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white flex items-center justify-center hover:bg-white/25 transition-colors animate-bounce">
              <ChevronDown size={18} />
            </button>
          )}
        </div>
      </div>
    </DeckContext.Provider>
  );
}
